import { useCallback, useEffect, useState } from "react";
import { Loader2, Save, Sparkles } from "lucide-react";
import { GrowTextarea } from "@/app/components/shared/GrowTextarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    generateLauraStage,
    getLauraStageState,
    patchLauraStageContext,
    patchLauraStageValidation,
    updateLauraDeliverable,
} from "@/app/lib/auditflowApi";
import type { LauraAudit, LauraStageId } from "@/app/components/shared/types";
import type { LauraStageState } from "@/app/lib/auditflowApi";
import type {
    LauraStageContext,
    LauraStageValidationItem,
} from "./lauraStageTypes";
import { PlanningProgressHeader } from "./PlanningProgressHeader";
import { ValidationCheckpointPanel } from "./ValidationCheckpointPanel";
import { useTranslation } from "@/app/i18n/LocaleContext";

type Props<TForm extends Record<string, unknown>> = {
    audit: LauraAudit;
    stageId: LauraStageId;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: LauraStageState | null) => void;
    defaultForm: () => TForm;
    title: string;
    description?: string;
    generateLabel: string;
    children: (ctx: {
        form: TForm;
        setForm: (updater: (prev: TForm) => TForm) => void;
        stageState: LauraStageState<TForm> | null;
        saveForm: () => Promise<void>;
    }) => React.ReactNode;
};

export function LauraStageShell<TForm extends Record<string, unknown>>({
    audit,
    stageId,
    deliverableContent,
    onDeliverableSaved,
    onStageStateChange,
    defaultForm,
    title,
    description,
    generateLabel,
    children,
}: Props<TForm>) {
    const { t } = useTranslation();
    const [stageState, setStageState] = useState<LauraStageState<TForm> | null>(
        null,
    );
    const [form, setFormState] = useState<TForm>(defaultForm);
    const [briefing, setBriefing] = useState(deliverableContent);
    const [validationDraft, setValidationDraft] = useState<
        LauraStageValidationItem[]
    >([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState("inputs");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const state = await getLauraStageState<TForm>(audit.id, stageId);
            setStageState(state);
            setFormState(state.stage.form_data ?? defaultForm());
            setBriefing(
                state.deliverable?.content_md ?? deliverableContent ?? "",
            );
            setValidationDraft(state.stage.validation_items ?? []);
            onStageStateChange?.(state);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [audit.id, stageId, deliverableContent, defaultForm, onStageStateChange]);

    useEffect(() => {
        void load();
    }, [load]);

    function setForm(updater: (prev: TForm) => TForm) {
        setFormState((prev) => updater(prev));
    }

    async function saveForm() {
        setSaving(true);
        setError(null);
        try {
            const { stage } = await patchLauraStageContext(audit.id, stageId, {
                form_data: form,
            });
            const next = stageState
                ? { ...stageState, stage: stage as LauraStageContext<TForm> }
                : null;
            setStageState(next);
            onStageStateChange?.(next);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        } finally {
            setSaving(false);
        }
    }

    async function handleGenerate() {
        setGenerating(true);
        setError(null);
        try {
            await saveForm();
            const result = await generateLauraStage(audit.id, stageId);
            const next = stageState
                ? {
                      ...stageState,
                      stage: result.stage as LauraStageContext<TForm>,
                  }
                : null;
            setStageState(next);
            setBriefing(result.content_md);
            setValidationDraft(result.validation_items ?? []);
            if (result.stage.form_data) {
                setFormState(result.stage.form_data as TForm);
            }
            onDeliverableSaved(result.content_md);
            onStageStateChange?.(next);
            setActiveTab("deliverable");
        } catch (e) {
            setError(e instanceof Error ? e.message : "Generation failed");
        } finally {
            setGenerating(false);
        }
    }

    async function saveBriefing() {
        setSaving(true);
        try {
            await updateLauraDeliverable(audit.id, stageId, briefing);
            onDeliverableSaved(briefing);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        } finally {
            setSaving(false);
        }
    }

    function toggleValidation(item: LauraStageValidationItem, checked: boolean) {
        const next = validationDraft.map((v) =>
            v.id === item.id ? { ...v, checked } : v,
        );
        setValidationDraft(next);
        void patchLauraStageValidation(audit.id, stageId, item.id, checked).catch(
            () => setValidationDraft(validationDraft),
        );
        setStageState((s) => {
            if (!s) return s;
            const updated = {
                ...s,
                stage: {
                    ...s.stage,
                    validation_items: next,
                },
            };
            onStageStateChange?.(updated);
            return updated;
        });
    }

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const saiName =
        (audit as LauraAudit & { laura_sai_profiles?: { name: string } })
            .laura_sai_profiles?.name ?? undefined;

    return (
        <div className="flex h-full flex-col overflow-hidden bg-background">
            <div className="flex flex-col gap-3 border-b px-4 py-3">
                <PlanningProgressHeader
                    currentStage={stageId}
                    saiName={saiName}
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h2 className="text-base font-semibold">{title}</h2>
                        {description && (
                            <p className="text-sm text-muted-foreground">
                                {description}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void saveForm()}
                            disabled={saving}
                        >
                            {saving ? (
                                <Loader2 data-icon="inline-start" className="animate-spin" />
                            ) : (
                                <Save data-icon="inline-start" />
                            )}
                            {t("laura", "saveForm")}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => void handleGenerate()}
                            disabled={generating}
                        >
                            {generating ? (
                                <Loader2 data-icon="inline-start" className="animate-spin" />
                            ) : (
                                <Sparkles data-icon="inline-start" />
                            )}
                            {generateLabel}
                        </Button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="border-b bg-destructive/10 px-4 py-2 text-sm text-destructive">
                    {error}
                </div>
            )}

            <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="flex min-h-0 flex-1 flex-col"
            >
                <div className="border-b px-4 py-2">
                    <TabsList>
                        <TabsTrigger value="inputs">
                            {t("laura", "tabInputs")}
                        </TabsTrigger>
                        <TabsTrigger value="deliverable">
                            {t("laura", "deliverable")}
                        </TabsTrigger>
                        <TabsTrigger value="validation">
                            {t("laura", "tabValidation")}
                            {validationDraft.length > 0 && (
                                <span className="ml-1 text-[10px] text-muted-foreground">
                                    ({validationDraft.filter((v) => v.checked).length}/
                                    {validationDraft.length})
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent
                    value="inputs"
                    className="min-h-0 flex-1 overflow-y-auto p-4"
                >
                    {children({ form, setForm, stageState, saveForm })}
                </TabsContent>

                <TabsContent
                    value="deliverable"
                    className="flex min-h-0 flex-1 flex-col"
                >
                    <div className="flex items-center justify-between border-b px-4 py-2">
                        <span className="text-sm font-medium">
                            {t("laura", "deliverable")}
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void saveBriefing()}
                            disabled={saving}
                        >
                            {t("laura", "saveDeliverable")}
                        </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4">
                        <GrowTextarea
                            className="min-h-[320px] w-full rounded-md border px-3 py-2 font-mono text-sm"
                            value={briefing}
                            onChange={(e) => setBriefing(e.target.value)}
                        />
                    </div>
                </TabsContent>

                <TabsContent
                    value="validation"
                    className="min-h-0 flex-1 overflow-y-auto p-4"
                >
                    <ValidationCheckpointPanel
                        items={validationDraft}
                        onToggle={toggleValidation}
                    />
                    {validationDraft.length === 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm">
                                    {t("laura", "validationEmpty")}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm text-muted-foreground">
                                {t("laura", "validationGenerateHint")}
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>

            <Separator />
        </div>
    );
}

export function useStageApprovalGates(
    stageState: { stage: { validation_items?: LauraStageValidationItem[] } } | null,
    deliverableContent: string,
) {
    const items = stageState?.stage.validation_items ?? [];
    const allValidation =
        items.length > 0 && items.every((i) => i.checked);
    const hasDeliverable = deliverableContent.trim().length > 0;
    return {
        canApprove: hasDeliverable && allValidation,
        allValidation,
        hasDeliverable,
    };
}
